import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import priceListApiParsers from './priceListApiParsers';

// ---------- API Slice (merged) ----------
const priceListApi = createApi({
  reducerPath: 'priceListApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['PriceList', 'PriceListsData'],
  endpoints: (builder) => ({
    getPriceList: builder.query<any, void>({
      query: () => {
        return {
          url: '/pricelist/pricelists',
          method: 'GET',
        };
      },
      transformResponse: (response: any) => priceListApiParsers.getPriceList(response),
    }),

    getPriceListProducts: builder.query<any[], { priceListId: number }>({
      query: ({ priceListId }) => {
        return {
          url: `/pricelist/pricelists/${priceListId}/products`,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => priceListApiParsers.getPriceListProducts(response),
      providesTags: ['PriceListsData'],
      keepUnusedDataFor: 0,
    }),

    addPriceListProduct: builder.mutation<
      any,
      { priceListId: number; productId: number; portId: number; price: number }
    >({
      query: (payload) => ({
        url: '/pricelist/products',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['PriceListsData'],
    }),

    updatePriceListProduct: builder.mutation<
      any,
      { priceListId: number; productId: number; portId: number; price: number }
    >({
      query: ({ priceListId, productId, portId, price }) => ({
        url: `/pricelist/products/${encodeURIComponent(priceListId)}/${encodeURIComponent(productId)}/${encodeURIComponent(portId)}`,
        method: 'PUT',
        data: { price },
      }),
      invalidatesTags: ['PriceListsData'],
    }),

    uploadPriceList: builder.mutation<
      any,
      {
        customerId: number;
        supplierId: number;
        currencyId: number; // will be 1 for USD
        dateFrom: string; // yyyy-MM-dd
        dateTo: string; // yyyy-MM-dd
        documentNb: string | number;
        file: File;
      }
    >({
      query: ({ customerId, supplierId, currencyId, dateFrom, dateTo, documentNb, file }) => {
        const form = new FormData();
        form.append('file', file);
        return {
          url: `/pricelist/upload`,
          method: 'POST',
          data: form,
          params: { customerId, supplierId, currencyId, dateFrom, dateTo, documentNb },
          contentType: 'MULTIPART',
        } as any;
      },
    }),

    getTempPriceLists: builder.query<any, { isValid?: boolean }>({
      query: ({ isValid = false }) => ({
        url: `/pricelist/tempPriceLists`,
        method: 'GET',
        params: { isValid },
      }),
    }),

    processValidRecords: builder.mutation<any, { tempPriceListId: number | string }>({
      query: ({ tempPriceListId }) => ({
        url: `/pricelist/processValidRecords/${encodeURIComponent(String(tempPriceListId))}`,
        method: 'POST',
      }),
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const priceListReducer = priceListApi.reducer;
export const priceListMiddleware = priceListApi.middleware;
export const priceListReducerPath = priceListApi.reducerPath;

// ---------- Hooks / Actions ----------
export const {
  useGetPriceListQuery,
  useLazyGetPriceListProductsQuery,
  useAddPriceListProductMutation,
  useUpdatePriceListProductMutation,
  useUploadPriceListMutation,
  useGetTempPriceListsQuery,
  useLazyGetTempPriceListsQuery,
  useProcessValidRecordsMutation,
} = priceListApi;
